/* eslint-disable react-hooks/exhaustive-deps */
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  HeaderGroup,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import {
  Box,
  Button,
  ButtonProps,
  Flex,
  Icon,
  Link,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Space,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { CircleOIcon, ErrorIcon, SuccessIcon } from '@tonic-one/react-icons';
import { useToggle } from '@tonic-one/react-hooks';
import { useEffect, useMemo, useState } from 'react';
import ProvisionModal from '../ProvisionModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PolicyEnableStatus,
  PolicyModeString,
  ProvisionStatusEnum,
  ServicesStatusListRes,
} from '@Types/provision';
import { ProvisionApi, PolicyApi } from '@Apis';
import { useTranslation } from 'react-i18next';
import { PolicySubTypeEnum } from '../../../types';
import { PermissionNameEnum } from '@Types/permissions';
import { MailServices } from '../../../utils/const';
import config from '../../../config';
import { handleRedirectClick, isInUIC } from '@Utils/common';
import AdvancedTooltipModal from '../AdvanceModal';
import { PolicyTypeEnum } from '@Policy/utils/constants';
import {
  AdvancedFeatureTypeEnum,
  AdvancedFeatureInfo,
  Service,
} from '@Types/app';
import {
  advancedStyle,
  AdvancedTooltipPopover,
} from '@Dashboard/AdvanceModal/advancedTooltip';
import { useLicenseStore } from '@Models/useLicenseModel';
import { useOrgStore } from '@Models/useOrgModel';
import { useAccountStore } from '@Models/useAccountModel';
import { useGlobalSettingStore } from '@Models/useGlobalSettingModel';
import { usePermissionStore } from '@Models/usePermissionModel';
import { removeUrlState } from '@Log/shared';

interface IdSyncTask {
  task: string;
  status: string;
  rawStatus: string;
}
interface TData {
  service: Service;
  provisionStatus: ProvisionStatusEnum;
  policyEnableStatus: PolicyEnableStatus[];
  orgId?: string;
  type: 'Email' | 'Collaboration';
  idSyncTasks?: IdSyncTask[];
}

const ProvisioningIcon = () => {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.22656 0.5625C9.33073 0.5625 10.3672 0.770833 11.3359 1.1875C12.3047 1.61458 13.1484 2.19271 13.8672 2.92188C14.5964 3.64062 15.1745 4.48438 15.6016 5.45312C16.0182 6.42188 16.2266 7.45833 16.2266 8.5625C16.2266 9.16667 16.1641 9.74479 16.0391 10.2969C15.9245 10.8594 15.7474 11.3906 15.5078 11.8906C15.2786 12.3906 14.9974 12.8542 14.6641 13.2812C14.3307 13.7188 13.9505 14.1146 13.5234 14.4688C13.1797 14.7917 12.8047 15.0833 12.3984 15.3438C12.0026 15.6042 11.5807 15.8229 11.1328 16C10.6953 16.1771 10.2318 16.3177 9.74219 16.4219C9.2526 16.5156 8.7474 16.5625 8.22656 16.5625C7.1224 16.5625 6.08594 16.3542 5.11719 15.9375C4.14844 15.5104 3.29948 14.9375 2.57031 14.2188C1.85156 13.4896 1.27865 12.6406 0.851562 11.6719C0.434896 10.7031 0.226562 9.66667 0.226562 8.5625C0.226562 7.54167 0.408854 6.57812 0.773438 5.67188C1.13802 4.76562 1.63281 3.96354 2.25781 3.26562C2.89323 2.56771 3.63802 1.98958 4.49219 1.53125C5.33594 1.07292 6.2474 0.78125 7.22656 0.65625V2.15625C6.45573 2.28125 5.73177 2.53125 5.05469 2.90625C4.38802 3.28125 3.80469 3.75 3.30469 4.3125C2.8151 4.875 2.42969 5.51562 2.14844 6.23438C1.86719 6.96354 1.72656 7.73958 1.72656 8.5625C1.72656 9.45833 1.89844 10.3021 2.24219 11.0938C2.57552 11.8854 3.03385 12.5781 3.61719 13.1719C4.21094 13.7552 4.90365 14.2135 5.69531 14.5469C6.48698 14.8906 7.33073 15.0625 8.22656 15.0625C9.02865 15.0625 9.79427 14.9062 10.5234 14.5938C11.263 14.2708 11.9297 13.8594 12.5234 13.3594L8.22656 8.5625V0.5625Z"
        fill="#578AEF"
      />
    </svg>
  );
};

const ProvisionNotStartIcon = () => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  return <Icon as={CircleOIcon} color={colorStyle.color.tertiary} />;
};

const provisionStatusString = {
  [ProvisionStatusEnum.NotStarted]:
    'dashboard.provision.table.provisionStatus.not',
  [ProvisionStatusEnum.Provisioned]:
    'dashboard.provision.table.provisionStatus.done',
  [ProvisionStatusEnum.Provisioning]:
    'dashboard.provision.table.provisionStatus.ing',
  [ProvisionStatusEnum.Failure]: '',

  // just for exchange poc
  [ProvisionStatusEnum.TokenCreated]:
    'dashboard.provision.table.provisionStatus.ing',
};

const provisionStatusIconMap = {
  [ProvisionStatusEnum.NotStarted]: <ProvisionNotStartIcon />,
  [ProvisionStatusEnum.Provisioned]: <Icon as={SuccessIcon} color="#04C45A" />,
  [ProvisionStatusEnum.Provisioning]: <ProvisioningIcon />,
  [ProvisionStatusEnum.Failure]: <Icon as={ErrorIcon} color="#F24C4F" />,

  // just for exchange poc
  [ProvisionStatusEnum.TokenCreated]: <ProvisioningIcon />,
};

const SHOW_SYNC_PROGRESS_SERVICES = [
  Service.Exchange,
  Service.OneDrive,
  Service.SharePoint,
  Service.Teams,
];

const ProvisionTable = () => {
  const { t } = useTranslation(['dashboard']);
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [queryParameters, setQueryParameters] = useSearchParams();
  const [modalState, setModalState] = useState<{
    serviceType: Service;
  } | null>(null);
  const { switchOrgId, orgsMap, orgId, defaultSingleOrgId } = useOrgStore();
  const { groupCompanyGuid } = useAccountStore();
  if (defaultSingleOrgId) {
    switchOrgId(defaultSingleOrgId);
  }
  const { selectedOrgGlobalSetting } = useGlobalSettingStore();
  const { hasSalesforceLicense } = useLicenseStore();

  const [data, setData] = useState<TData[]>([]);
  const [isLoading, toggleLoading] = useToggle(false);
  const [refreshTrigger, setRefreshTrigger] = useState<null | Date>(null);
  const [advanceTooltipModalState, setAdvanceTooltipModalState] = useState<{
    isOpen: boolean;
    serviceType?: Service;
  }>({
    isOpen: false,
  });
  const [advanceData, setAdvanceData] = useState<AdvancedFeatureInfo>({
    needConfirm: true,
    avaliableCreit: 0,
    aadUserCount: 0,
  });

  const advanceProtectionInfo = {
    advancedProtectionServiceList: [
      t('dashboard.serviceStatus.advancedTooltip.inline'),
      t('dashboard.serviceStatus.advancedTooltip.va'),
      t('common.service.gmailInline'),
    ],
    advancedTooltipShouldShowList: [
      Service.ExchangeInline,
      Service.GmailInline,
    ],
    creditsPerDefaultNumber: 25,
  };

  function removeUrlQueryParams() {
    if (isInUIC()) {
      removeUrlState();
    }
    setQueryParameters('');
  }

  const fetchData = async () => {
    const HIDE_SERVICE = [
      Service.ExchangeServer,
      Service.Mip,
      Service.IdentityProtection,
      ...(hasSalesforceLicense
        ? []
        : [Service.SalesforceProduction, Service.SalesforceSandbox]),
    ];

    const EMAIL_SERVICES = [
      Service.Exchange,
      Service.ExchangeInline,
      Service.Gmail,
      Service.GmailInline,
    ];
    toggleLoading(true);
    ProvisionApi.apiGetServicesStatusList()
      .then((res) => {
        const data: TData[] = [];
        let key: keyof ServicesStatusListRes;
        for (key in res.data) {
          const item = res.data[key];
          if (
            (!selectedOrgGlobalSetting.gmailInlineFeatureShow &&
              key === Service.GmailInline) ||
            (!selectedOrgGlobalSetting.exchangeInlineFeatureShow &&
              key === Service.ExchangeInline)
          ) {
            continue;
          }
          if (item) {
            data.push({
              service: key,
              provisionStatus: item.provisionStatus.status,
              policyEnableStatus: item.policyEnableStatus,
              type: EMAIL_SERVICES.includes(key) ? 'Email' : 'Collaboration',
              idSyncTasks: item.provisionStatus?.idSync?.tasks as
                | IdSyncTask[]
                | undefined,
            });
          }
        }
        setData(
          data.filter((item) => {
            return !HIDE_SERVICE.includes(item.service);
          })
        );
      })
      .catch(() => {
        setData([]);
      })
      .finally(() => {
        toggleLoading(false);
      });
  };

  useEffect(() => {
    if (!refreshTrigger) {
      return;
    }
    fetchData();
  }, [refreshTrigger]);

  useEffect(() => {
    fetchData();
  }, [orgId]);

  const columns: ColumnDef<TData>[] = useMemo(
    () => [
      {
        id: 'service',
        header: t('dashboard.provision.table.header.service'),
        accessorKey: 'service',
        cell: ({ getValue }) => <>{t(`common.service.${getValue()}`)}</>,
        size: 240,
        meta: {
          customColumnStyle: {
            width: '25%',
          },
        },
      },
      {
        id: 'provisionStatus',
        header: t('dashboard.provision.table.header.connectionStatus'),
        accessorKey: 'provisionStatus',
        size: 150,
        meta: {
          customColumnStyle: {
            width: '18%',
            textAlign: 'left',
          },
        },
        cell: ({ row }) => {
          const status = row.original.provisionStatus as ProvisionStatusEnum;

          const { idSyncTasks, service } = row.original;

          if (SHOW_SYNC_PROGRESS_SERVICES.includes(service) && idSyncTasks) {
            let rate: undefined | string = '';
            const rawStatus = idSyncTasks
              .map((t) => t.rawStatus)
              .find((t) => (rate = extraRateFromCurrentStatus(t)));

            if (rawStatus && rate) {
              return (
                <Flex align="center" gap="2x">
                  {provisionStatusIconMap['provisioning']}
                  {t('dashboard.serviceStatus.syncProgress', { rate })}
                </Flex>
              );
            }
          }

          return (
            <Flex align="center" gap="2x">
              {provisionStatusIconMap[status]}
              {t(provisionStatusString[status])}
            </Flex>
          );
        },
      },
      {
        id: 'policyEnableStatus',
        header: t('dashboard.provision.table.header.policyStatus'),
        accessorFn: ({ policyEnableStatus, service, provisionStatus }) => {
          if (provisionStatus !== ProvisionStatusEnum.Provisioned) {
            return t('dashboard.provision.table.policyStatus.noData');
          }
          return getPolicyEnableStatusString(t, policyEnableStatus, service);
        },
        size: 150,
        meta: {
          customColumnStyle: {
            width: '30%',
          },
        },
      },
      {
        id: 'action',
        header: t('dashboard.provision.table.header.action'),
        accessorKey: 'provisionStatus',
        size: 240,
        meta: {
          customColumnStyle: {
            width: '27%',
          },
        },
        cell: ({ row }) => (
          <ActionButton
            row={row}
            setModalState={setModalState}
            advanceProtectionInfo={advanceProtectionInfo}
            setAdvanceTooltipModalState={setAdvanceTooltipModalState}
            setAdvanceData={setAdvanceData}
          />
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    defaultColumn: {
      minSize: 40,
    },
    getCoreRowModel: getCoreRowModel(),
  });
  // If you want to manage your own state and override the state manager, you can uncomment and modify the following code:
  /*
  const [state, setState] = React.useState(table.initialState);
  table.setOptions(prevOptions => ({
    ...prevOptions,
    state,
    onStateChange: setState,
  }));
  */

  const onModalClose = () => {
    setModalState(null);
    setRefreshTrigger(new Date());
  };

  useEffect(() => {
    const orgIdInQuery = queryParameters.get('orgId');
    const serviceInQuery = queryParameters.get('service');
    if (orgIdInQuery && orgsMap.has(orgIdInQuery) && serviceInQuery) {
      switchOrgId(orgIdInQuery);
      if (
        advanceProtectionInfo.advancedTooltipShouldShowList.includes(
          serviceInQuery as Service
        ) &&
        isInUIC()
      ) {
        PolicyApi.getAdvancedFeatureInfo(
          AdvancedFeatureTypeEnum.provision,
          (serviceInQuery as Service) === Service.ExchangeInline
            ? PolicyTypeEnum.ExchangeInline
            : PolicyTypeEnum.GmailInline
        )
          .then((res) => {
            if (res.data.needConfirm) {
              setAdvanceData(res.data);
              setAdvanceTooltipModalState({
                isOpen: true,
                serviceType: serviceInQuery as Service,
              });
            } else {
              setModalState({
                serviceType: serviceInQuery as Service,
              });
            }
          })
          .catch((error) => {
            console.log(JSON.stringify(`error: ${error}`));
          });
      } else {
        setModalState({
          serviceType: serviceInQuery as Service,
        });
      }
    } else {
      if (orgId === 'all') {
        switchOrgId(groupCompanyGuid);
      }
    }
    removeUrlQueryParams();
  }, []);

  const layout = 'flexbox'; // One of: 'flexbox', 'table'

  return (
    <>
      {isLoading ? (
        <Flex flex={1}>
          <Spinner mx="auto" mt="20vh" />
        </Flex>
      ) : (
        <Table
          layout={layout}
          width="100%"
          background={colorMode === 'dark' ? '' : colorStyle.background.primary}
        >
          <SubTable
            rows={table.getRowModel().rows}
            headers={table.getHeaderGroups()}
            advanceProtectionInfo={advanceProtectionInfo}
          />
        </Table>
      )}
      {modalState !== null && (
        <ProvisionModal
          modalState={modalState}
          orgId={orgId}
          isOpen={modalState !== null}
          onClose={onModalClose}
        />
      )}
      <AdvancedTooltipModal
        isVisible={advanceTooltipModalState.isOpen}
        onClose={() => {
          setAdvanceTooltipModalState({ isOpen: false });
        }}
        onConfirm={() => {
          const service = advanceTooltipModalState.serviceType;
          setAdvanceTooltipModalState({ isOpen: false });
          setModalState({
            serviceType: service as Service,
          });
        }}
        advanceData={advanceData}
        advanceFeatureType={AdvancedFeatureTypeEnum.provision}
      ></AdvancedTooltipModal>
    </>
  );
};

const SubTable = ({
  rows,
  headers,
  advanceProtectionInfo,
}: {
  rows: Row<TData>[];
  headers: HeaderGroup<TData>[];
  advanceProtectionInfo: any;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation(['dashboard']);

  const servicesRows: {
    emailServices: Row<TData>[];
    collaborationServices: Row<TData>[];
  } = {
    emailServices: [],
    collaborationServices: [],
  };

  rows.reduce((acc, curValue) => {
    if (curValue.original.type === 'Email') {
      acc.emailServices.push(curValue);
    } else {
      acc.collaborationServices.push(curValue);
    }
    return acc;
  }, servicesRows);

  const ShareHeader = () => {
    return (
      <TableHeader width="100%">
        {headers.map((headerGroup) => (
          <TableHeaderRow key={headerGroup.id} width="100%">
            {headerGroup.headers.map((header) => {
              const styleProps = {
                minWidth: header.column.columnDef.minSize,
                width: header.getSize(),
                ...header.column.columnDef.meta?.customColumnStyle,
              };
              return (
                <TableHeaderCell key={header.id} {...styleProps}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHeaderCell>
              );
            })}
          </TableHeaderRow>
        ))}
      </TableHeader>
    );
  };

  const emailServiceOrder = [
    Service.Exchange,
    Service.ExchangeInline,
    Service.Gmail,
    Service.GmailInline,
  ];

  const collaborationServiceOrder = [
    Service.OneDrive,
    Service.SharePoint,
    Service.Teams,
    Service.TeamsChat,
    Service.GoogleDrive,
    Service.Box,
    Service.Dropbox,
    Service.SalesforceSandbox,
    Service.SalesforceProduction,
    Service.ExchangeServer,
  ];

  return (
    <>
      {servicesRows.emailServices.length > 0 && (
        <>
          <TableHeader>
            <TableHeaderRow width="100%">
              <TableHeaderCell
                width="100%"
                color={colorStyle.color.primary}
                backgroundColor={colorStyle.background.tertiary}
                textTransform="uppercase"
              >
                {t('dashboard.provision.table.title.emailServices')}
              </TableHeaderCell>
            </TableHeaderRow>
          </TableHeader>
          <ShareHeader />
          <CommonTableBody
            rows={servicesRows.emailServices.sort(
              (a, b) =>
                emailServiceOrder.indexOf(a.original.service) -
                emailServiceOrder.indexOf(b.original.service)
            )}
            advanceProtectionInfo={advanceProtectionInfo}
          />
          <Space height="24px" />
        </>
      )}
      {servicesRows.collaborationServices.length > 0 && (
        <>
          <TableHeader>
            <TableHeaderRow width="100%">
              <TableHeaderCell
                width="100%"
                color={colorStyle.color.primary}
                backgroundColor={colorStyle.background.tertiary}
                textTransform="uppercase"
              >
                {t('dashboard.provision.table.title.collaborationServices')}
              </TableHeaderCell>
            </TableHeaderRow>
          </TableHeader>
          <ShareHeader />
          <CommonTableBody
            rows={servicesRows.collaborationServices.sort(
              (a, b) =>
                collaborationServiceOrder.indexOf(a.original.service) -
                collaborationServiceOrder.indexOf(b.original.service)
            )}
            advanceProtectionInfo={advanceProtectionInfo}
          />
        </>
      )}
    </>
  );
};

const CommonTableBody = ({
  rows,
  advanceProtectionInfo,
}: {
  rows: Row<TData>[];
  advanceProtectionInfo: any;
}) => {
  const [colorMode] = useColorMode();
  const { t } = useTranslation();
  const hoverBackgroundColor = {
    dark: 'rgba(255, 255, 255, 0.12)',
    light: 'rgba(0, 0, 0, 0.12)',
  }[colorMode];
  return (
    <TableBody>
      {rows.map((row) => (
        <TableRow
          alignItems="center"
          width="100%"
          height="40px"
          key={row.id}
          _hover={{
            backgroundColor: hoverBackgroundColor,
          }}
        >
          {row.getVisibleCells().map((cell) => {
            const styleProps = {
              minWidth: cell.column.columnDef.minSize,
              width: cell.column.getSize(),
              ...cell.column.columnDef.meta?.customColumnStyle,
            };
            return (
              <TableCell key={cell.id} {...styleProps} height="40px">
                {cell.column.columnDef.id === 'service' &&
                advanceProtectionInfo.advancedTooltipShouldShowList.includes(
                  row.original.service
                ) &&
                isInUIC() ? (
                  <Flex>
                    <AdvancedTooltipPopover
                      cell={cell}
                      advanceProtectionInfo={advanceProtectionInfo}
                    ></AdvancedTooltipPopover>
                    <sup style={advancedStyle}>
                      {t(
                        'dashboard.serviceStatus.enableAdvancedProtection.superscript'
                      )}
                    </sup>
                  </Flex>
                ) : (
                  flexRender(cell.column.columnDef.cell, cell.getContext())
                )}
              </TableCell>
            );
          })}
        </TableRow>
      ))}
    </TableBody>
  );
};

const ActionButton = ({
  row,
  setModalState,
  advanceProtectionInfo,
  setAdvanceTooltipModalState,
  setAdvanceData,
}: {
  row: Row<TData>;
  setModalState: (state: { serviceType: Service }) => void;
  advanceProtectionInfo: any;
  setAdvanceTooltipModalState: (state: {
    isOpen: boolean;
    serviceType?: Service;
  }) => void;
  setAdvanceData: (state: AdvancedFeatureInfo) => void;
}) => {
  const navigate = useNavigate();
  const { provisionStatus, policyEnableStatus } = row.original;
  const { widthEditablePermission } = usePermissionStore();
  const { hasEmailAddOnLicense } = useLicenseStore();
  const administratorEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const { t } = useTranslation('dashboard');
  const [isButtonClicked, setIsButtonClicked] = useState(false);

  const isInlineService = [
    Service.ExchangeInline,
    Service.GmailInline,
  ].includes(row.original.service);

  const style = {
    disabled:
      !administratorEditPermission ||
      (isInlineService && !hasEmailAddOnLicense),
    size: 'sm' as ButtonProps['size'],
  };
  if (provisionStatus === ProvisionStatusEnum.NotStarted) {
    return (
      <Flex>
        <Popover
          placement="top"
          trigger="hover"
          flexShrink={0}
          disabled={hasEmailAddOnLicense || !isInlineService}
        >
          <PopoverTrigger {...{ shouldWrapChildren: true }}>
            <Button
              size={'sm' as ButtonProps['size']}
              disabled={
                isButtonClicked ||
                !administratorEditPermission ||
                (isInlineService && !hasEmailAddOnLicense)
              }
              onClick={() => {
                setIsButtonClicked(true);
                if (
                  advanceProtectionInfo.advancedTooltipShouldShowList.includes(
                    row.original.service
                  ) &&
                  isInUIC()
                ) {
                  PolicyApi.getAdvancedFeatureInfo(
                    AdvancedFeatureTypeEnum.provision,
                    row.original.service === Service.ExchangeInline
                      ? PolicyTypeEnum.ExchangeInline
                      : PolicyTypeEnum.GmailInline
                  )
                    .then((res) => {
                      if (res.data.needConfirm) {
                        setAdvanceData(res.data);
                        setAdvanceTooltipModalState({
                          isOpen: true,
                          serviceType: row.original.service,
                        });
                      } else {
                        setModalState({
                          serviceType: row.original.service,
                        });
                      }
                    })
                    .catch((error) => {
                      console.log(JSON.stringify(`error: ${error}`));
                    })
                    .finally(() => setIsButtonClicked(false));
                } else {
                  setModalState({
                    serviceType: row.original.service,
                  });
                  setIsButtonClicked(false);
                }
              }}
            >
              {t('dashboard.provision.table.action.provision')}
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <NoAddOnLicense />
          </PopoverContent>
        </Popover>
      </Flex>
    );
  } else if (provisionStatus === ProvisionStatusEnum.Provisioning) {
    return (
      <Button
        variant="secondary"
        onClick={() => {
          setModalState({
            serviceType: row.original.service,
          });
        }}
        {...style}
      >
        {t('dashboard.provision.table.action.viewStatus')}
      </Button>
    );
  } else if (provisionStatus === ProvisionStatusEnum.Provisioned) {
    if (
      (policyEnableStatus[0].atp === 'none' &&
        policyEnableStatus.length === 1) ||
      (policyEnableStatus.length === 2 &&
        policyEnableStatus[0].atp === 'none' &&
        policyEnableStatus[1].atp === 'none')
    ) {
      return (
        <Button
          variant="secondary"
          onClick={() => {
            const collectionType = MailServices.includes(row.original.service)
              ? 'email'
              : 'collaboration';
            const url = `/policy/advanced-threat-protection/${collectionType}`;
            if (isInUIC()) {
              handleRedirectClick(url);
            } else {
              navigate(url);
            }
          }}
          {...style}
        >
          {t('dashboard.provision.table.action.configurePolicy')}
        </Button>
      );
    } else {
      return (
        <Button
          variant="secondary"
          onClick={() => {
            window.location.href = `${config.ui.prefix}/dashboard?type=configurationHealth`;
          }}
          {...style}
        >
          {t('dashboard.provision.table.action.assessConfigurationHealth')}
        </Button>
      );
    }
  } else if (provisionStatus === ProvisionStatusEnum.Failure) {
    return (
      <Button
        variant="secondary"
        onClick={() => {
          setModalState({
            serviceType: row.original.service,
          });
        }}
        {...style}
      >
        {t('dashboard.provision.table.action.retry')}
      </Button>
    );
  }
  return (
    <Button
      variant="secondary"
      onClick={() => {
        setModalState({
          serviceType: row.original.service,
        });
      }}
      {...style}
    >
      {t('dashboard.provision.table.action.viewStatus')}
    </Button>
  );
};

export default ProvisionTable;

const getPolicyEnableStatusString = (
  t: Function,
  policyState: PolicyEnableStatus[],
  service: Service
): string => {
  //just for atp
  const arr = [
    PolicyModeString.DefaultPolicy,
    PolicyModeString.DefaultMonitorPolicy,
    PolicyModeString.NormalPolicy,
  ] as string[];

  if (service === Service.GmailInline || service === Service.ExchangeInline) {
    let inboundAtpStatus:
      | Omit<PolicyModeString, PolicyModeString.DefaultXDRMonitorPolicy>
      | 'none' = 'none';
    let outboundAtpStatus:
      | Omit<PolicyModeString, PolicyModeString.DefaultXDRMonitorPolicy>
      | 'none' = 'none';
    if (policyState[0].policySubtype === PolicySubTypeEnum.inbound) {
      inboundAtpStatus = policyState[0].atp;
      outboundAtpStatus = policyState[1].atp;
    } else {
      outboundAtpStatus = policyState[0].atp;
      inboundAtpStatus = policyState[1].atp;
    }
    if (inboundAtpStatus === 'none' && outboundAtpStatus === 'none') {
      return t('dashboard.provision.table.policyStatus.atp.none');
    } else if (inboundAtpStatus === 'none') {
      if (arr.includes(outboundAtpStatus as string)) {
        return t(
          `dashboard.provision.table.policyStatus.outboundAtp.${outboundAtpStatus}`
        );
      }
    } else if (outboundAtpStatus === 'none') {
      if (arr.includes(inboundAtpStatus as string)) {
        return t(
          `dashboard.provision.table.policyStatus.inboundAtp.${inboundAtpStatus}`
        );
      }
    }
    //have both outbound and inbound policy, just show inbound atp
    return t(
      `dashboard.provision.table.policyStatus.inboundAtp.${inboundAtpStatus}`
    );
  } else {
    const { atp } = policyState[0];
    if (arr.includes(atp)) {
      return t(`dashboard.provision.table.policyStatus.atp.${atp}`);
    }
  }
  return t('dashboard.provision.table.policyStatus.atp.none');
};

const NoAddOnLicense = () => {
  const { t } = useTranslation('provision');
  return (
    <Box>
      <Text>{t('provision.common.noLicense')}</Text>
      <Link target="_blank" href={config.ui.loginPage.contactReseller}>
        {t('provision.common.getLicense')}
      </Link>
    </Box>
  );
};

const extraRateFromCurrentStatus = (status: string) => {
  return /^PENDING;RemainingTime=\d+;Rate=(\d+)$/.exec(status)?.[1];
};
